import {
  type RevisionColor,
  type RevisionColorKey,
  type RevisionLabel,
  type RevisionScheme,
  revisionColorBase,
  revisionColorSchema,
  revisionColorTier,
  type VersionFormat,
} from '@noconflict/contracts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { OUTLINED_REVISION_SWATCHES } from '@/shared/ui/tokens';

// whole class names, so Tailwind sees them
const swatchClasses: Record<RevisionColor, string> = {
  white: 'bg-revision-white',
  blue: 'bg-revision-blue',
  pink: 'bg-revision-pink',
  yellow: 'bg-revision-yellow',
  green: 'bg-revision-green',
  goldenrod: 'bg-revision-goldenrod',
  buff: 'bg-revision-buff',
  salmon: 'bg-revision-salmon',
  cherry: 'bg-revision-cherry',
  tan: 'bg-revision-tan',
  lavender: 'bg-revision-lavender',
};

/** A colour key as people say it: "Blue", "Double blue". */
export function useRevisionColorLabel(): (key: RevisionColorKey) => string {
  const { t } = useTranslation('common');
  return useCallback(
    (key: RevisionColorKey) => {
      const base = revisionColorBase(key);
      const tier = revisionColorTier(key);
      return tier === 'single'
        ? t(`revisionColor.names.${base}`)
        : t(`revisionColor.tiers.${tier}`, { color: t(`revisionColor.inline.${base}`) });
    },
    [t],
  );
}

/**
 * A revision row's label as the pill and running text show it: a colour's name under `color`; the label's own
 * text under `custom` ("Unmarked" for the set with no label); "Changed pages" under `changed`, or "All pages" when
 * the version is a pages copy.
 */
export function useRevisionLabel(): (
  scheme: RevisionScheme,
  label: RevisionLabel,
  format?: VersionFormat,
) => string {
  const { t } = useTranslation('common');
  const colorLabel = useRevisionColorLabel();
  return useCallback(
    (scheme: RevisionScheme, label: RevisionLabel, format?: VersionFormat) => {
      if (scheme === 'changed')
        return format === 'pages' ? t('revision.allPages') : t('revision.changed');
      if (label === null) return t('revision.unmarked');
      const color = scheme === 'color' ? revisionColorSchema.safeParse(label) : null;
      return color?.success ? colorLabel(color.data) : label.trim();
    },
    [t, colorLabel],
  );
}

/**
 * One revision row of a script version. Under `color` a swatch in the colour's hue plus its name, so colour is
 * never the only signal (white and buff swatches are outlined to show on a light surface); under `custom` a
 * neutral chip with the label's text, or "Unmarked"; under `changed` "Changed pages" or "All pages".
 */
export function RevisionPill({
  scheme,
  label,
  format,
  className,
}: {
  scheme: RevisionScheme;
  label: RevisionLabel;
  format?: VersionFormat;
  className?: string;
}) {
  const text = useRevisionLabel();
  const color = scheme === 'color' ? revisionColorSchema.safeParse(label) : null;
  const key = color?.success ? color.data : null;
  const base = key ? revisionColorBase(key) : null;
  const outlined =
    base !== null && (OUTLINED_REVISION_SWATCHES as readonly string[]).includes(base);
  return (
    <span
      data-slot="revision-pill"
      data-scheme={scheme}
      data-color={key ?? undefined}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-bold text-xs',
        base ? 'bg-card text-foreground' : 'bg-muted text-foreground',
        className,
      )}
    >
      {base ? (
        <span
          data-slot="revision-swatch"
          aria-hidden="true"
          className={cn(
            'size-2.5 shrink-0 rounded-full',
            swatchClasses[base],
            outlined && 'ring-1 ring-muted-foreground ring-inset',
          )}
        />
      ) : null}
      {text(scheme, label, format)}
    </span>
  );
}
